import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import App from "./App.tsx";
import "./index.css";
import { initFrontendPerfObservers } from "./lib/frontendPerf";

// Start collecting Web Vitals (LCP, CLS, INP) and long-task metrics
// from the very first paint so the admin Performance dashboard has
// real data to show.
initFrontendPerfObservers();

// Note: the Ramayan Kosh dictionary (manifest + shards) is no longer
// prefetched at boot. It loads lazily the first time the Ramcharitmanas
// reader, search panel, or a glossary word is rendered — see
// `prefetchRamayanKoshShardsForText`, `ensureShardsForWord`, and
// `lookupRamayanKoshWord`, which each call `ensureManifest()` on demand.

// Auto-recover from stale Vite chunks after dep re-optimization / HMR restarts.
// When Vite restarts (e.g. .env changes), the browser's cached dynamic import
// URLs (?v=<hash>) become invalid and React.lazy throws
// "Failed to fetch dynamically imported module". A one-shot hard reload
// transparently recovers without the user seeing a blank screen.
const isStaleChunkError = (msg: unknown) =>
  typeof msg === "string" &&
  (msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("Unable to preload CSS") ||
    /Loading (chunk|CSS chunk) [^ ]+ failed/i.test(msg));

const RELOAD_KEY = "__vite_stale_chunk_reload_at__";
const RELOAD_COOLDOWN_MS = 10_000;

const reloadOnce = () => {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  window.location.reload();
};

const extractMessage = (val: unknown): string => {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (val instanceof Error) return val.message;
  if (typeof val === "object" && "message" in val) return String((val as { message: unknown }).message ?? "");
  return String(val);
};

window.addEventListener("error", (e) => {
  if (isStaleChunkError(extractMessage(e.error ?? e.message))) reloadOnce();
});
window.addEventListener("unhandledrejection", (e) => {
  if (isStaleChunkError(extractMessage(e.reason))) reloadOnce();
});

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>,
);
